import { useEffect, useMemo } from "react";
import { Controller, useWatch, type Control, type UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { BidRequestFormValues, CommodityOption, UnitOfMeasureOption } from "../../types";
import { getCommodityUnitOptions } from "../../utils";

interface QualityOption {
  value: string;
  label: string;
}

interface CommodityLineRowProps {
  index: number;
  control: Control<BidRequestFormValues>;
  setValue: UseFormSetValue<BidRequestFormValues>;
  commodities: CommodityOption[];
  units: UnitOfMeasureOption[];
  qualityOptions: QualityOption[];
  canRemove: boolean;
  onRemove: () => void;
  isDisabled: boolean;
}

export const CommodityLineRow = ({
  index,
  control,
  setValue,
  commodities,
  units,
  qualityOptions,
  canRemove,
  onRemove,
  isDisabled,
}: CommodityLineRowProps) => {
  const { t } = useTranslation();

  const commodityValue = useWatch({ control, name: `items.${index}.commodity` });
  const unitValue = useWatch({ control, name: `items.${index}.unit_of_measure` });
  const allItems = useWatch({ control, name: "items" });

  const unitOptions = useMemo(() => {
    const commodity = commodities.find(item => String(item.id) === commodityValue);
    return getCommodityUnitOptions(commodity, units);
  }, [commodities, commodityValue, units]);

  const availableCommodities = useMemo(() => {
    const takenCommodityIds = new Set(
      (allItems ?? [])
        .filter((_, itemIndex) => itemIndex !== index)
        .map(item => item?.commodity)
        .filter(Boolean)
    );
    return commodities.filter(commodity => !takenCommodityIds.has(String(commodity.id)));
  }, [allItems, commodities, index]);

  const hasMultipleUnits = unitOptions.length > 1;
  const singleUnitCode = unitOptions.length === 1 ? unitOptions[0].code : "";

  useEffect(() => {
    if (!commodityValue) {
      return;
    }
    const isCurrentValid = unitOptions.some(unit => String(unit.id) === unitValue);
    if (!isCurrentValid) {
      setValue(`items.${index}.unit_of_measure`, unitOptions[0] ? String(unitOptions[0].id) : "", {
        shouldValidate: false,
      });
    }
  }, [commodityValue, unitOptions, unitValue, index, setValue]);

  return (
    <div className="flex flex-wrap items-start gap-4">
      <Controller
        control={control}
        name={`items.${index}.commodity`}
        render={({ field, fieldState }) => (
          <Field className="min-w-56 flex-1">
            <FieldLabel htmlFor={`commodity-${index}`}>
              {t("bidRequests.form.commodity")}
            </FieldLabel>
            <Combobox
              id={`commodity-${index}`}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              options={availableCommodities.map(commodity => ({
                value: String(commodity.id),
                label: commodity.name,
              }))}
              placeholder={t("bidRequests.form.commodityPlaceholder")}
              searchPlaceholder={t("bidRequests.form.commoditySearchPlaceholder")}
              emptyText={t("bidRequests.form.commodityEmpty")}
              disabled={isDisabled}
              invalid={!!fieldState.error}
            />
            <FieldError>{fieldState.error?.message}</FieldError>
          </Field>
        )}
      />

      <Controller
        control={control}
        name={`items.${index}.quality`}
        render={({ field, fieldState }) => (
          <Field className="min-w-40 flex-1">
            <FieldLabel htmlFor={`quality-${index}`}>{t("bidRequests.form.quality")}</FieldLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={isDisabled}>
              <SelectTrigger
                id={`quality-${index}`}
                aria-invalid={!!fieldState.error}
                onBlur={field.onBlur}
                className="w-full border-border"
              >
                <SelectValue placeholder={t("bidRequests.form.qualityPlaceholder")} />
              </SelectTrigger>
              <SelectContent position="popper">
                {qualityOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError>{fieldState.error?.message}</FieldError>
          </Field>
        )}
      />

      <Controller
        control={control}
        name={`items.${index}.quantity`}
        render={({ field, fieldState }) => (
          <Field className="w-40">
            <FieldLabel htmlFor={`quantity-${index}`}>{t("bidRequests.form.quantity")}</FieldLabel>
            <div className="relative">
              <Input
                {...field}
                id={`quantity-${index}`}
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                disabled={isDisabled}
                aria-invalid={!!fieldState.error}
                className="border-border pr-12"
              />
              {singleUnitCode && (
                <span className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 text-muted-foreground text-sm">
                  {singleUnitCode}
                </span>
              )}
            </div>
            <FieldError>{fieldState.error?.message}</FieldError>
          </Field>
        )}
      />

      {hasMultipleUnits && (
        <Controller
          control={control}
          name={`items.${index}.unit_of_measure`}
          render={({ field, fieldState }) => (
            <Field className="w-32">
              <FieldLabel htmlFor={`unit-${index}`}>{t("bidRequests.form.unit")}</FieldLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={isDisabled}>
                <SelectTrigger
                  id={`unit-${index}`}
                  aria-invalid={!!fieldState.error}
                  onBlur={field.onBlur}
                  className="w-full border-border"
                >
                  <SelectValue placeholder={t("bidRequests.form.unit")} />
                </SelectTrigger>
                <SelectContent position="popper">
                  {unitOptions.map(unit => (
                    <SelectItem key={unit.id} value={String(unit.id)}>
                      {unit.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>{fieldState.error?.message}</FieldError>
            </Field>
          )}
        />
      )}

      <WFPButton
        type="button"
        variant="ghost"
        size="icon"
        className="mt-6 text-muted-foreground"
        aria-label={t("bidRequests.form.removeCommodity")}
        disabled={isDisabled || !canRemove}
        onClick={onRemove}
      >
        <Trash2 className="size-4" />
      </WFPButton>
    </div>
  );
};
